import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PanelTooltipProps {
    title: string;
}

/**
 * A mobile-safe tooltip that renders via React portal directly to document.body
 */
const PanelTooltip: React.FC<PanelTooltipProps> = ({ title }) => {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
    const lastTouchRef = useRef(0);

    const isGhostEvent = () => Date.now() - lastTouchRef.current < 350;

    const computePos = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        setPos({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right
        });
    };

    const show = () => {
        computePos();
        setOpen(true);
    };

    const hide = () => setOpen(false);

    useEffect(() => {
        if (!open) return;
        const handler = () => {
            if (isGhostEvent()) return;
            hide();
        };
        document.addEventListener('touchstart', handler);
        document.addEventListener('mousedown', handler);
        return () => {
            document.removeEventListener('touchstart', handler);
            document.removeEventListener('mousedown', handler);
        };
    }, [open]);

    return (
        <>
            <button
                ref={btnRef}
                className="panel-tooltip-btn"
                onMouseEnter={() => {
                    if (isGhostEvent()) return;
                    show();
                }}
                onMouseLeave={() => {
                    if (isGhostEvent()) return;
                    hide();
                }}
                onTouchStart={(e) => {
                    e.stopPropagation();
                    lastTouchRef.current = Date.now();
                    setOpen((prev) => {
                        const next = !prev;
                        if (next) computePos();
                        return next;
                    });
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <i className="bi bi-question-circle" />
            </button>

            {open &&
                pos &&
                createPortal(
                    <div
                        className="panel-tooltip-popup"
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            right: pos.right,
                            zIndex: 9999,
                            pointerEvents: 'none'
                        }}
                    >
                        {title}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default PanelTooltip;
